import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { ProfileData, BillingData, ProfileProps } from '../types/components';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const Profile: React.FC<ProfileProps> = ({
  initialProfileData,
  initialBillingData,
  onSaveProfile,
  onSaveCard,
  sidebarCollapsed = false,
  hasSidebar = false,
}) => {
  const navigate = useNavigate();
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const { userData, currentUser } = useAuth() as any;

  // Profile State
  const [fullName, setFullName] = useState<string>(initialProfileData?.fullName || userData?.fullName || currentUser?.displayName || '');
  const [email, setEmail] = useState<string>(initialProfileData?.email || userData?.email || currentUser?.email || '');
  const [role] = useState<string>(initialProfileData?.role || userData?.role || 'User'); // Role is read-only
  const [region, setRegion] = useState<string>(initialProfileData?.region || userData?.region || 'us');
  const [additionalRegions, setAdditionalRegions] = useState<string[]>(
    initialProfileData?.additionalRegions || userData?.additionalRegions || ['EU', 'US']
  );

  // Sync state with userData when it loads
  React.useEffect(() => {
    if (userData) {
      setFullName(prev => prev === 'Jane Doe' || !prev ? (userData.fullName || currentUser?.displayName || '') : prev);
      setEmail(prev => prev === 'jane.doe@example.com' || !prev ? (userData.email || currentUser?.email || '') : prev);
      // Only update if not already modified or if default
      if (userData.region) setRegion(userData.region);
      if (userData.additionalRegions) setAdditionalRegions(userData.additionalRegions);
    }
  }, [userData, currentUser]);

  // Billing State
  const [cardholderName, setCardholderName] = useState(initialBillingData?.cardholderName || '');
  const [cardNumber, setCardNumber] = useState(initialBillingData?.cardNumber || '');
  const [expiryMonth, setExpiryMonth] = useState(initialBillingData?.expiryMonth || '');
  const [expiryYear, setExpiryYear] = useState(initialBillingData?.expiryYear || '');
  const [cvv, setCvv] = useState(initialBillingData?.cvv || '');
  const [billingCountry, setBillingCountry] = useState(initialBillingData?.billingCountry || '');
  const [billingAddress, setBillingAddress] = useState(initialBillingData?.billingAddress || '');

  // Validation Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Tag Input State
  const [tagInput, setTagInput] = useState('');

  // Format card number with spaces every 4 digits
  const formatCardNumber = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Limit to 16 digits
    const limited = digits.slice(0, 16);
    // Add space every 4 digits
    return limited.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  // Handle card number input
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
  };

  // Handle tag input
  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toUpperCase();
      if (!additionalRegions.includes(newTag)) {
        setAdditionalRegions([...additionalRegions, newTag]);
        setTagInput('');
      }
    }
  };

  // Remove tag
  const removeTag = (tagToRemove: string) => {
    setAdditionalRegions(additionalRegions.filter(tag => tag !== tagToRemove));
  };

  // Validate email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate card number (basic format check)
  const validateCardNumber = (cardNumber: string): boolean => {
    const digits = cardNumber.replace(/\s/g, '');
    return digits.length >= 13 && digits.length <= 19 && /^\d+$/.test(digits);
  };

  // Validate CVV
  const validateCVV = (cvv: string): boolean => {
    return /^\d{3,4}$/.test(cvv);
  };

  // Validate expiry date
  const validateExpiryDate = (month: string, year: string): boolean => {
    if (!month || !year) return false;
    const expiryDate = new Date(parseInt(year), parseInt(month) - 1);
    const now = new Date();
    return expiryDate > now;
  };

  // Validate profile form
  const validateProfile = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!validateEmail(email)) {
      newErrors.email = 'Invalid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate billing form
  const validateBilling = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (cardholderName && !cardholderName.trim()) {
      newErrors.cardholderName = 'Cardholder name is required';
    }

    if (cardNumber && !validateCardNumber(cardNumber)) {
      newErrors.cardNumber = 'Invalid card number';
    }

    if (cardNumber && (!expiryMonth || !expiryYear)) {
      newErrors.expiryDate = 'Expiry date is required';
    }

    if (cardNumber && expiryMonth && expiryYear && !validateExpiryDate(expiryMonth, expiryYear)) {
      newErrors.expiryDate = 'Expiry date must be in the future';
    }

    if (cardNumber && !validateCVV(cvv)) {
      newErrors.cvv = 'CVV must be 3-4 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save profile
  const handleSaveProfile = async () => {
    if (validateProfile()) {
      const profileData: ProfileData = {
        fullName,
        email,
        role,
        region,
        additionalRegions,
      };

      try {
        if (currentUser) {
          const userRef = doc(db, 'users', currentUser.uid);
          await updateDoc(userRef, {
            fullName,
            region,
            additionalRegions,
            updatedAt: new Date().toISOString()
          });
          // Optional: Show success toast
          console.log('Profile updated successfully');
        }

        onSaveProfile?.(profileData);
      } catch (error) {
        console.error('Error updating profile:', error);
      }
    }
  };

  // Handle save card
  const handleSaveCard = () => {
    if (validateBilling()) {
      const billingData: BillingData = {
        cardholderName,
        cardNumber,
        expiryMonth,
        expiryYear,
        cvv,
        billingCountry,
        billingAddress,
      };
      onSaveCard?.(billingData);
      // Show success message (you can add a toast notification here)
    }
  };

  const handleBackToChat = () => {
    navigate('/app#chat');
  };

  return (
    <main
      className="main-content flex-1 flex flex-col items-center justify-start py-[48px] px-[32px] overflow-y-auto transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
      style={{
        marginLeft: hasSidebar ? (sidebarCollapsed ? '80px' : '260px') : '0',
        height: '100vh',
        maxHeight: '100vh',
      }}
    >
      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .main-content {
            padding: 24px 16px;
          }
          .profile-card {
            padding: 24px !important;
          }
        }
      `}</style>

      <div
        className="profile-card w-full max-w-[800px] rounded-2xl md:rounded-[24px] p-5 md:p-8 lg:p-[48px] animate-[fadeInUp_0.6s_ease-out]"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Back to Chat Button */}
        <button
          type="button"
          onClick={handleBackToChat}
          className="back-to-chat-btn flex items-center gap-2 mb-6 md:mb-[32px] px-3 md:px-4 py-2 md:py-2.5 rounded-[12px] border border-[rgba(255,255,255,0.15)] bg-[rgba(255,255,255,0.05)] text-white text-[14px] font-medium cursor-pointer transition-all duration-300 hover:bg-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.2)] hover:-translate-y-0.5 active:scale-[0.98]"
          style={{
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          }}
          aria-label="Back to Chat"
        >
          <ArrowLeft size={16} className="flex-shrink-0" />
          <span className="whitespace-nowrap">Back to Chat</span>
        </button>

        {/* Section A: Personal Information */}
        <section className="profile-section">
          <h2 className="section-header text-xl md:text-2xl lg:text-3xl font-semibold text-white mb-6 md:mb-8 lg:mb-[32px]">
            Profile Details
          </h2>

          <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-[32px] mb-6 md:mb-8 lg:mb-[32px]">
            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="fullName"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                className="form-input w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-base md:text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] placeholder:text-[#888888]"
                style={{ minHeight: '48px', fontSize: '16px' }}
                aria-label="Full Name"
                aria-required="true"
              />
              {errors.fullName && (
                <span className="text-red-400 text-xs mt-1">{errors.fullName}</span>
              )}
            </div>

            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="email"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane.doe@example.com"
                readOnly
                className="form-input w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] placeholder:text-[#888888] opacity-60 cursor-not-allowed"
                aria-label="Email"
              />
              {errors.email && (
                <span className="text-red-400 text-xs mt-1">{errors.email}</span>
              )}
            </div>

            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="role"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Role
              </label>
              <div
                className="role-badge inline-flex items-center p-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.15)] rounded-[12px] text-white text-[14px] opacity-60 cursor-not-allowed"
              >
                {role}
              </div>
            </div>

            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="region"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Region
              </label>
              <select
                id="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="form-select w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-base md:text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                style={{ minHeight: '48px', fontSize: '16px' }}
                aria-label="Region"
              >
                <option value="us">United States</option>
                <option value="eu">European Union</option>
                <option value="uk">United Kingdom</option>
                <option value="ca">Canada</option>
                <option value="au">Australia</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group full-width col-span-2 max-md:col-span-1 flex flex-col gap-[8px]">
              <label
                htmlFor="additionalRegions"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Additional Regions
              </label>
              <div
                className="tag-input-container min-h-[56px] bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-[8px] flex flex-wrap gap-[8px] items-center transition-all duration-300 focus-within:border-white focus-within:shadow-[0_0_15px_rgba(255,255,255,0.1)]"
              >
                {additionalRegions.map((tag, index) => (
                  <span
                    key={index}
                    className="tag inline-flex items-center gap-[8px] px-3 py-1.5 bg-[rgba(255,255,255,0.1)] rounded-full text-[12px] text-white transition-all duration-200"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="tag-remove bg-transparent border-none text-[#888888] cursor-pointer text-[14px] leading-none p-0 w-4 h-4 flex items-center justify-center rounded-full transition-all duration-200 hover:bg-[rgba(255,255,255,0.1)] hover:text-white"
                      aria-label={`Remove ${tag} tag`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  id="tagInput"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  placeholder="Type and press Enter to add region..."
                  className="tag-input flex-1 min-w-[120px] bg-transparent border-none outline-none text-white text-base md:text-[14px] font-inherit p-[8px] placeholder:text-[#888888]"
                  style={{ fontSize: '16px', minHeight: '40px' }}
                  aria-label="Add additional region"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section C: Billing & Payment */}
        {/* <section className="profile-section billing-section mt-8 md:mt-12 lg:mt-[48px]">
          <h2 className="section-header text-xl md:text-2xl lg:text-3xl font-semibold text-white mb-6 md:mb-8 lg:mb-[32px]">
            Billing & Payment
          </h2>

          <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-[32px] mb-6 md:mb-8 lg:mb-[32px]">
            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="cardholderName"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Cardholder Name
              </label>
              <input
                type="text"
                id="cardholderName"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                placeholder="John Doe"
                className="form-input w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] placeholder:text-[#888888]"
                aria-label="Cardholder Name"
              />
              {errors.cardholderName && (
                <span className="text-red-400 text-xs mt-1">{errors.cardholderName}</span>
              )}
            </div>

            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="cardNumber"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Card Number
              </label>
              <input
                type="text"
                id="cardNumber"
                value={cardNumber}
                onChange={handleCardNumberChange}
                placeholder="1234 5678 9012 3456"
                maxLength={19}
                className="form-input w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] placeholder:text-[#888888] tracking-[2px] font-mono"
                aria-label="Card Number"
              />
              {errors.cardNumber && (
                <span className="text-red-400 text-xs mt-1">{errors.cardNumber}</span>
              )}
            </div>

            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="expiryMonth"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Expiration Month
              </label>
              <select
                id="expiryMonth"
                value={expiryMonth}
                onChange={(e) => setExpiryMonth(e.target.value)}
                className="form-select w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                aria-label="Expiration Month"
              >
                <option value="">Month</option>
                {Array.from({ length: 12 }, (_, i) => {
                  const month = String(i + 1).padStart(2, '0');
                  return (
                    <option key={month} value={month}>
                      {month}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="expiryYear"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Expiration Year
              </label>
              <select
                id="expiryYear"
                value={expiryYear}
                onChange={(e) => setExpiryYear(e.target.value)}
                className="form-select w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                aria-label="Expiration Year"
              >
                <option value="">Year</option>
                {Array.from({ length: 11 }, (_, i) => {
                  const year = 2025 + i;
                  return (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  );
                })}
              </select>
              {errors.expiryDate && (
                <span className="text-red-400 text-xs mt-1">{errors.expiryDate}</span>
              )}
            </div>

            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="cvv"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                CVV
              </label>
              <input
                type="password"
                id="cvv"
                value={cvv}
                onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="123"
                maxLength={4}
                className="form-input w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] placeholder:text-[#888888]"
                aria-label="CVV"
              />
              {errors.cvv && (
                <span className="text-red-400 text-xs mt-1">{errors.cvv}</span>
              )}
            </div>

            <div className="form-group flex flex-col gap-[8px]">
              <label
                htmlFor="billingCountry"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Country
              </label>
              <select
                id="billingCountry"
                value={billingCountry}
                onChange={(e) => setBillingCountry(e.target.value)}
                className="form-select w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                aria-label="Billing Country"
              >
                <option value="">Select your country</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="IT">Italy</option>
                <option value="ES">Spain</option>
                <option value="NL">Netherlands</option>
                <option value="BE">Belgium</option>
                <option value="CH">Switzerland</option>
                <option value="AT">Austria</option>
                <option value="SE">Sweden</option>
                <option value="NO">Norway</option>
                <option value="DK">Denmark</option>
                <option value="FI">Finland</option>
                <option value="IE">Ireland</option>
                <option value="PL">Poland</option>
                <option value="CZ">Czech Republic</option>
                <option value="PT">Portugal</option>
                <option value="GR">Greece</option>
                <option value="TR">Turkey</option>
                <option value="RU">Russia</option>
                <option value="JP">Japan</option>
                <option value="CN">China</option>
                <option value="KR">South Korea</option>
                <option value="IN">India</option>
                <option value="SG">Singapore</option>
                <option value="MY">Malaysia</option>
                <option value="TH">Thailand</option>
                <option value="ID">Indonesia</option>
                <option value="PH">Philippines</option>
                <option value="VN">Vietnam</option>
                <option value="NZ">New Zealand</option>
                <option value="BR">Brazil</option>
                <option value="MX">Mexico</option>
                <option value="AR">Argentina</option>
                <option value="CL">Chile</option>
                <option value="CO">Colombia</option>
                <option value="PE">Peru</option>
                <option value="ZA">South Africa</option>
                <option value="EG">Egypt</option>
                <option value="NG">Nigeria</option>
                <option value="KE">Kenya</option>
                <option value="AE">United Arab Emirates</option>
                <option value="SA">Saudi Arabia</option>
                <option value="IL">Israel</option>
                <option value="QA">Qatar</option>
                <option value="KW">Kuwait</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div className="form-group full-width col-span-2 max-md:col-span-1 flex flex-col gap-[8px]">
              <label
                htmlFor="billingAddress"
                className="form-label text-[11px] font-medium uppercase tracking-[0.1em] text-[#666666] mb-[4px]"
              >
                Billing Address
              </label>
              <textarea
                id="billingAddress"
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
                rows={3}
                placeholder="123 Main Street, City, State, ZIP Code"
                className="form-input w-full bg-transparent border border-[rgba(255,255,255,0.15)] rounded-[12px] p-4 text-white text-[14px] font-inherit outline-none transition-all duration-300 focus:border-white focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] placeholder:text-[#888888] resize-y min-h-[80px]"
                aria-label="Billing Address"
              />
            </div>
          </div>

          <div
            className="billing-security flex items-center gap-4 mt-6 p-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-[12px]"
          >
            <div className="security-indicator flex items-center gap-2 text-[#22c55e] text-[12px] font-medium">
              <Shield size={16} />
              <span>Secure payment</span>
            </div>
            <p className="security-text text-[12px] text-[#888888] m-0">
              Your payment information is encrypted and secure
            </p>
          </div>

          <div className="action-area flex justify-end mt-8 md:mt-12 lg:mt-[48px] pt-6 md:pt-8 lg:pt-[32px] border-t border-[rgba(255,255,255,0.1)]">
            <button
              type="button"
              onClick={handleSaveCard}
              className="btn-save px-6 md:px-8 lg:px-[32px] py-2.5 md:py-3 bg-white text-[#050505] border-none rounded-full text-[14px] font-semibold font-inherit cursor-pointer transition-all duration-300 shadow-[0_4px_16px_rgba(255,255,255,0.2)] hover:scale-105 hover:shadow-[0_6px_24px_rgba(255,255,255,0.3)] active:scale-[0.98]"
            >
              Save Card
            </button>
          </div>
        </section> */}

        {/* Action Area for Profile Details */}
        <div className="action-area flex justify-end mt-8 md:mt-12 lg:mt-[48px] pt-6 md:pt-8 lg:pt-[32px] border-t border-[rgba(255,255,255,0.1)]">
          <button
            type="button"
            onClick={handleSaveProfile}
            className="btn-save px-6 md:px-8 lg:px-[32px] py-2.5 md:py-3 bg-white text-[#050505] border-none rounded-full text-[14px] font-semibold font-inherit cursor-pointer transition-all duration-300 shadow-[0_4px_16px_rgba(255,255,255,0.2)] hover:scale-105 hover:shadow-[0_6px_24px_rgba(255,255,255,0.3)] active:scale-[0.98]"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Fade In Up Animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-\\[fadeInUp_0\\.6s_ease-out\\] {
          animation: fadeInUp 0.6s ease-out;
        }
      `}</style>
    </main>
  );
};

export default Profile;

